import type { Chart as ApiChart } from '../../api/types';
import type { LayoutItem } from 'react-grid-layout';
import {
  CHART_LAYOUT_DEFAULTS,
  DEFAULT_CHART_LAYOUT,
  GRID_COLS,
} from './types';

/**
 * Get default layout constraints for a chart type.
 */
export function getDefaultsForChartType(chartType: string) {
  return CHART_LAYOUT_DEFAULTS[chartType] ?? DEFAULT_CHART_LAYOUT;
}

/**
 * Auto-place charts in a grid, filling rows left-to-right.
 * Ignores any saved positions — always computes fresh layout.
 */
function autoPlace(charts: ApiChart[]): LayoutItem[] {
  const layout: LayoutItem[] = [];
  let currentX = 0;
  let currentY = 0;
  let rowMaxH = 0;

  for (const chart of charts) {
    const defaults = getDefaultsForChartType(chart.chartType || 'line');
    const w = defaults.w;
    const h = defaults.h;

    // Move to next row if this widget doesn't fit
    if (currentX + w > GRID_COLS) {
      currentX = 0;
      currentY += rowMaxH;
      rowMaxH = 0;
    }

    layout.push({
      i: chart.id,
      x: currentX,
      y: currentY,
      w,
      h,
      minH: defaults.minH,
      maxH: defaults.maxH,
      minW: defaults.minW,
    });

    currentX += w;
    rowMaxH = Math.max(rowMaxH, h);
  }

  return layout;
}

/**
 * Auto-arrange all charts — always computes fresh layout (ignores saved positions).
 * Used by the "Auto arrange" action and reset.
 *
 * Places KPIs first (4 per row), then regular charts (2 per row).
 */
export function autoArrangeLayout(charts: ApiChart[]): LayoutItem[] {
  if (!charts.length) return [];

  const isKpi = (c: ApiChart) => {
    const t = c.chartType as string;
    return t === 'kpi' || t === 'kpi_wide';
  };
  const kpis = charts
    .filter(isKpi)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const regular = charts
    .filter((c) => !isKpi(c))
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const kpiLayout = autoPlace(kpis);
  const regularLayout = autoPlace(regular);

  const kpiBottomY = kpiLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  const offsetRegular = regularLayout.map((item) => ({
    ...item,
    y: item.y + kpiBottomY,
  }));

  return [...kpiLayout, ...offsetRegular];
}

/**
 * Convert an array of charts into a grid layout.
 *
 * Walks charts in `orderIndex` order.  Charts with a saved
 * `positionConfig` keep their position.  Charts without one
 * (e.g. a freshly duplicated chart) are placed directly below
 * the preceding chart, so they appear right after their original.
 *
 * When no chart has a saved position, delegates to `autoArrangeLayout`
 * for the KPI-first grouping behaviour.
 */
export function chartsToLayout(charts: ApiChart[]): LayoutItem[] {
  if (!charts.length) return [];

  // Fast path: no saved positions at all → full auto-arrange
  if (charts.every((c) => !c.positionConfig)) {
    return autoArrangeLayout(charts);
  }

  // Sort by orderIndex so duplicates (orderIndex = original + 1)
  // are processed right after their original.
  const sorted = [...charts].sort(
    (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
  );

  const layout: LayoutItem[] = [];
  let prevItem: LayoutItem | null = null;
  // Cumulative vertical offset from inserted (unpositioned) charts.
  // Applied only to positioned charts whose saved y >= the insertion point.
  let yOffset = 0;
  let insertionThresholdY = Infinity;

  for (const chart of sorted) {
    const defaults = getDefaultsForChartType(chart.chartType || 'line');
    const pos = chart.positionConfig;

    let item: LayoutItem;

    if (pos) {
      // Use the saved position, shifted down if at or below the insertion point
      const adjustedY = pos.y >= insertionThresholdY ? pos.y + yOffset : pos.y;
      item = {
        i: chart.id,
        x: pos.x,
        y: adjustedY,
        w: pos.w,
        h: Math.max(defaults.minH, Math.min(pos.h, defaults.maxH)),
        minH: defaults.minH,
        maxH: defaults.maxH,
        minW: defaults.minW,
      };
    } else {
      // Place below the previous chart (or at origin if first)
      // Always use chart-type defaults for dimensions so a new bar chart
      // doesn't inherit a KPI's tiny size when it happens to follow one.
      const insertY = prevItem ? prevItem.y + prevItem.h : 0;
      const w = defaults.w;
      const h = defaults.h;
      item = {
        i: chart.id,
        x: prevItem?.x ?? 0,
        y: insertY,
        w,
        h,
        minH: defaults.minH,
        maxH: defaults.maxH,
        minW: defaults.minW,
      };
      // Track the insertion point so subsequent positioned charts shift down
      insertionThresholdY = Math.min(insertionThresholdY, insertY);
      yOffset += item.h;
    }

    layout.push(item);
    prevItem = item;
  }

  return layout;
}
